import BooleanFilter from "./BooleanFilter.vue";
/**
 * Shared ag-grid `columnTypes` for DataTable (client-side) and DataTableV2
 * (server-side / infinite row model). Extracted so both tables render the exact
 * same custom row values (cell renderers, filters, value formatters, comparators)
 * with no drift.
 *
 * Components are imported directly from their .vue files (not the folder barrel)
 * to avoid a circular import with index.js.
 */
import BooleanValue from "./BooleanValue.vue";
import CVEFilter from "./CVEFilter.vue";
import CVEValue from "./CVEValue.vue";
import DaysRemaining from "./DaysRemaining.vue";
import DualTickValue from "./DualTickValue.vue";
import IsOpenValue from "./IsOpenValue.vue";
import NotBooleanValue from "./NotBooleanValue.vue";
import NotificationTypeValue from "./NotificationTypeValue.vue";
import OpenStatusValue from "./OpenStatusValue.vue";
import PermissionValue from "./PermissionValue.vue";
import ResidualRisk from "./ResidualRisk.vue";
import ReviewValue from "./ReviewValue.vue";
import TickValue from "./TickValue.vue";
import TimeDistance from "./TimeDistance.vue";
import TrainingStatusValue from "./TrainingStatusValue.vue";

function isValidDate(date) {
  return (
    date &&
    Object.prototype.toString.call(date) === "[object Date]" &&
    !isNaN(date)
  );
}

function dateComparator(a, b) {
  let ta = isValidDate(a) ? a.getTime() : 0;
  let tb = isValidDate(new Date(b)) ? new Date(b).getTime() : 0;
  if (ta === 0 || tb === 0) return 0;
  return ta === tb ? 0 : ta < tb ? 1 : -1;
}

/**
 * Build a fresh `columnTypes` map. Returned as a factory so each grid instance
 * gets its own object (avoids any accidental shared-mutation between grids).
 */
export function createColumnTypes() {
  return {
    date: {
      filter: "agDateColumnFilter",
      useValueFormatterForExport: true,
      valueFormatter(params) {
        function isDate(s) {
          if (isNaN(s) && !isNaN(Date.parse(s))) return true;
          else return false;
        }

        if (isDate(params.value) || params.value instanceof Date) {
          return new Date(params.value).toLocaleDateString("en-GB", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
        } else {
          return params.value;
        }
      },
      filterParams: {
        comparator: dateComparator,
      },
    },
    bool: {
      cellRenderer: BooleanValue,
      filter: {
        component: BooleanFilter,
        doesFilterPass: (params) => params.handlerParams.getValue(params.node) === params.model.value,
      },
    },
    notbool: {
      cellRenderer: NotBooleanValue,
      filter: {
        component: BooleanFilter,
        doesFilterPass: (params) => params.handlerParams.getValue(params.node) === !params.model.value,
      },
    },
    residualRisk: {
      cellRenderer: ResidualRisk,
    },
    review: {
      cellRenderer: ReviewValue,
    },
    html: {
      valueFormatter(params) {
        return params.value.replace(/<[^>]*>/g, "").replace(/&[^;\s]+;/g, ' ').replace(/\s+/g, ' ').trim();
      },
    },
    currency: {
      valueFormatter(params) {
        // Create our number formatter.
        var formatter = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "gbp",
        });
        return formatter.format(parseFloat(params.value));
      },
    },
    cve: {
      cellRenderer: CVEValue,
      filter: {
        component: CVEFilter,
        doesFilterPass: (params) => {
          const model = params.model;
          const value = params.handlerParams.getValue(params.node);
          const CVE_VALUES = new Set(["CVE", "Compliance Verification Engineer"]);
          const KNOWN_VALUES = new Set(["Compiler", "Checker", "Approver", "CVE", "Compliance Verification Engineer"]);
          if (model.compiler && value === "Compiler") return true;
          if (model.checker  && value === "Checker")  return true;
          if (model.approver && value === "Approver") return true;
          if (model.cve      && CVE_VALUES.has(value)) return true;
          if (model.none     && !KNOWN_VALUES.has(value)) return true;
          return false;
        },
      },
    },
    number: {
      comparator(num1, num2) {
        return parseFloat(num2) - parseFloat(num1);
      },
    },
    openStatus: {
      cellRenderer: OpenStatusValue,
      filter: {
        component: BooleanFilter,
        doesFilterPass: (params) => params.handlerParams.getValue(params.node) === params.model.value,
      },
    },
    IsOpen: {
      cellRenderer: IsOpenValue,
      filter: {
        component: BooleanFilter,
        doesFilterPass: (params) => params.handlerParams.getValue(params.node) === params.model.value,
      },
    },
    notificationType: {
      cellRenderer: NotificationTypeValue,
    },
    daysRemaining: {
      cellRenderer: DaysRemaining,
        filter: "agDateColumnFilter",
    },
    timeDistance: {
      cellRenderer: TimeDistance,
      filter: "agDateColumnFilter",
      filterParams: {
        comparator: dateComparator,
      },
    },
    trainingStatus: {
      cellRenderer: TrainingStatusValue,
      sortable: false,
      filter: false,
    },
    tick: {
      cellRenderer: TickValue,
      sortable: false,
      filter: false,
    },
    dualTick: {
      cellRenderer: DualTickValue,
      sortable: false,
      filter: false,
    },
    permission: {
      cellRenderer: PermissionValue,
      sortable: false,
      filter: false,
    }
  };
}
